import PlanInfo from "features/settings/Billing/PlanInfo";
import { LanguageNameSpaces, SubscriptionType } from "@shared/types";
import { ConfigurationCard } from "features/settings/common/styles";
import Title from "common/Typography/Title";
import PaymentsTable from "features/settings/Billing/PaymentsTable";
import Tabs from "common/Tabs/Tabs";
import { useState } from "react";
import {
  MAX_SETTINGS_WIDTH,
  PROMO_CODE_QUERY_PARAM,
} from "features/settings/common/constants";
import styled, { useTheme } from "styled-components";
import NewLookBillingHistory from "features/settings/Billing/NewLookBillingHistory.tsx";
import {
  NewLookFeatureUsage,
  NewLookSignalTrades,
} from "features/settings/Billing/NewLookPlanUsage.tsx";
import { BILLING_TWO_COLUMN_WIDTH } from "features/settings/Billing/layout.ts";
import PlanUsage from "features/settings/Billing/PlanUsage";
import { BillingTab } from "@shared/features/settings/subscription/types";
import useQuery from "utils/hooks/useQuery.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

const CornixBillingPage = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const query = useQuery();
  const isBillingHistory = !!query.get(PROMO_CODE_QUERY_PARAM);
  const [tab, setTab] = useState<BillingTab>(
    isBillingHistory ? BillingTab.BILLING_HISTORY : BillingTab.PLAN_USAGE
  );
  const { isNewLook } = useTheme();

  const getPaymentsTable = (subscriptionType: SubscriptionType) => {
    return (
      <ConfigurationCard style={{ marginTop: 16 }}>
        <Title level={3} style={{ marginBottom: 16 }}>
          {t("billing_history_title")}
        </Title>
        <PaymentsTable subscriptionType={subscriptionType} />
      </ConfigurationCard>
    );
  };

  const getContent = () => {
    return (
      <StyledTabs
        activeKey={tab}
        onChange={setTab as any}
        items={[
          {
            label: t("tabs.plan_usage"),
            key: BillingTab.PLAN_USAGE,
            children: <PlanUsage />,
          },
          {
            label: t("tabs.billing_history"),
            key: BillingTab.BILLING_HISTORY,
            children: getPaymentsTable(SubscriptionType.PLATFORM),
          },
        ]}
      />
    );
  };

  if (isNewLook) {
    // Usage and history share one page; the history comes first when it
    // would have been the selected tab (e.g. right after a payment).
    const history = (
      <NewLookBillingHistory subscriptionType={SubscriptionType.PLATFORM} />
    );
    return (
      <NewLookContainer>
        <NewLookLayout>
          <NewLookMain>
            <PlanInfo />
            {isBillingHistory && history}
            <NewLookSignalTrades />
            {!isBillingHistory && history}
          </NewLookMain>
          <NewLookSide>
            <NewLookFeatureUsage />
          </NewLookSide>
        </NewLookLayout>
      </NewLookContainer>
    );
  }

  return (
    <>
      <PlanInfo />
      {getContent()}
    </>
  );
};

export default CornixBillingPage;

const StyledTabs = styled(Tabs)`
  margin-top: 16px;
  padding: 0;
  width: 100%;
  max-width: ${MAX_SETTINGS_WIDTH}px;
  .ant-tabs-tab {
    padding: 4px 0 12px 0;
  }
`;

/**
 * One column; with room (container query), plan, trades and history on the
 * left and the feature usage as a side list.
 */
const NewLookContainer = styled.div`
  container: billing / inline-size;
  min-width: 0;
`;
const NewLookLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 16px;
  align-items: start;
  @container billing (min-width: ${BILLING_TWO_COLUMN_WIDTH}px) {
    grid-template-columns: minmax(0, 1fr) 360px;
    gap: 16px 20px;
  }
`;
const NewLookMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  /* Narrow: everything stacks in one column, usage last. */
  @container billing (max-width: ${BILLING_TWO_COLUMN_WIDTH - 1}px) {
    display: contents;
  }
`;
const NewLookSide = styled.div`
  min-width: 0;
`;
